import React, { useState, useEffect } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useParams, useNavigate } from 'react-router-dom';
import { getProfileCompanyPicUrl } from '../../helpers/url_helper';
import { dataAPI } from '../../helpers/api_service';
import { Warning as WarningIcon } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  rectIntersection,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import emptyDocIcon from '../../assets/empty_doc.svg';
import TaskForm from './TaskForm';
import TaskCard from './TaskCard';
import AlertDialog from '../Common/AlertDialog';

// Helper function to handle profile picture URL (same as DashboardLayout)
const getProfilePicUrl = (profilePic) => {
  if (!profilePic) return '';
  
  console.log('getProfilePicUrl input:', profilePic);
  
  // Check if URL already starts with http (any protocol)
  if (profilePic.toLowerCase().startsWith('http://') || profilePic.toLowerCase().startsWith('https://')) {
    console.log('Returning absolute URL:', profilePic);
    return profilePic; // Return as is if it's already a full URL
  } else {
    const fullUrl = getProfileCompanyPicUrl(profilePic);
    console.log('Returning relative URL with prefix:', fullUrl);
    return fullUrl; // Append profile/company pic URL prefix if it's a relative path
  }
};

// Sortable Task Card Component
const SortableTaskCard = ({ task, onOptionsClick, onCardClick, status }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id.toString(),
    data: {
      type: 'task',
      task,
      status,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 2, 1)',
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <TaskCard
        task={task}
        onOptionsClick={onOptionsClick}
        onCardClick={onCardClick}
        isDragging={isDragging}
      />
    </div>
  );
};

// Droppable Column Component
const DroppableColumn = ({ status, children, mode }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `column-${status}`,
    data: {
      type: 'column',
      status: status,
    },
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        overflowY: 'auto',
                        padding: '4px',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        minHeight: '200px',
                        backgroundColor: isOver ? (mode === 'dark' ? '#2f2f2f' : '#f5f5f5') : 'transparent',
                        border: isOver ? '2px dashed #26C3C0' : 'none',
      }}
      data-status={status}
      data-column-id={`column-${status.toLowerCase().replace(/\s+/g, '-')}`}
      className={`kanban-column ${isOver ? 'drag-over' : ''}`}
    >
      {children}
    </div>
  );
};

const Tasks = () => {
  const { mode } = useThemeMode();
  const { hasAddPermission } = usePermissions();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [projectDetails, setProjectDetails] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [creatorProfilePic, setCreatorProfilePic] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [taskEnums, setTaskEnums] = useState({ priorities: [], statuses: [] });
  const [draggedTask, setDraggedTask] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState('');
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData'));
      if (userData && userData.company_detail) {
        setCompanyLogo(userData.company_detail.company_logo || null);
        setCompanyName(userData.company_detail.name || '');
      }
    } catch (e) {
      setCompanyLogo(null);
      setCompanyName('');
    }
  }, []);

  useEffect(() => {
    if (projectId) {
      fetchProjectTasks();
    }
  }, [projectId]);

  // Load task enums from localStorage
  useEffect(() => {
    try {
      const storedEnums = localStorage.getItem('taskEnums');
      if (storedEnums) {
        setTaskEnums(JSON.parse(storedEnums));
      }
    } catch (error) {
      console.error('Error loading task enums from localStorage:', error);
    }
  }, []);

  const fetchProjectTasks = async () => {
    setIsLoading(true);
    setError('');
    try {
      // Fetch tasks for this specific project
      const tasksResponse = await dataAPI.getTasksList(projectId);
      
      // Update to use new API response structure
      if (tasksResponse.status === 'success') {
        setTasks(tasksResponse.task_list || []);
        setProjectDetails({
          id: tasksResponse.project_details?.id || projectId,
          title: tasksResponse.project_details?.title || 'Project Title',
          description: tasksResponse.project_details?.description || '',
          start_date: tasksResponse.project_details?.start_date || '',
          end_date: tasksResponse.project_details?.end_date || '',
          status: tasksResponse.project_details?.status || '',
          created_by_username: tasksResponse.project_details?.creator_username || 'Unknown',
          created_by_profile_pic: tasksResponse.project_details?.creator_profile_pic || null,
          created_at: tasksResponse.project_details?.created_at || '',
          updated_at: tasksResponse.project_details?.updated_at || ''
        });
        
        // Set creator profile picture if available
        if (tasksResponse.project_details?.creator_profile_pic) {
          console.log('Setting creator profile pic:', tasksResponse.project_details.creator_profile_pic);
          setCreatorProfilePic(tasksResponse.project_details.creator_profile_pic);
        } else {
          console.log('No creator profile pic found in response');
        }
      } else {
        setTasks([]);
        setProjectDetails({
          id: projectId,
          title: 'Project Title',
          created_by_username: 'Unknown'
        });
      }
    } catch (error) {
      console.error('Error fetching project tasks:', error);
      setError('Failed to load tasks for this project. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewTask = (task) => {
    navigate(`/project/${projectId}/task/${task.id}`, {
      state: {
        projectStartDate: projectDetails?.start_date
      }
    });
  };

  const handleCreateTask = () => {
    setIsEditMode(false);
    setEditingTask(null);
    setIsFormOpen(true);
  };

  // Edit task handler
  const handleEditTask = (task) => {
    setIsEditMode(true);
    setEditingTask(task);
    setIsFormOpen(true);
  };

  // Delete task handler
  const handleDeleteTask = (task) => {
    setTaskToDelete(task);
    setDeleteDialogOpen(true);
  };

  // Confirm delete task handler
  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    
    // Clear any existing delete error
    setDeleteError('');
    
    try {
      await dataAPI.deleteTask(taskToDelete.id, projectId);
      // Refresh the tasks list
      fetchProjectTasks();
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
    } catch (error) {
      console.error('Error deleting task:', error);
      // Show custom toast error instead of browser alert
      setDeleteError(`Failed to delete task "${taskToDelete?.title}". Please try again.`);
      // Clear error message after 5 seconds
      setTimeout(() => {
        setDeleteError('');
      }, 5000);
      // Close the delete dialog
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
    }
  };

  // Handle drag and drop
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    try {
      // Get the new status from the destination column
      let newStatus = null;
      
      // Method 1: Try to get status from the over element's data
      if (over.data.current?.status) {
        newStatus = over.data.current.status;
      } 
      // Method 2: Check if over element is a column
      else if (over.id && over.id.startsWith('column-')) {
        newStatus = over.id.replace('column-', '');
      } 
      // Method 3: Try to find the status from the parent column element
      else {
        const columnElement = over.closest('[data-status]');
        if (columnElement) {
          newStatus = columnElement.getAttribute('data-status');
        }
      }
      
      if (!newStatus) {
        return;
      }
      
      // Find the task being moved
      const taskToMove = tasks
        .flatMap(statusGroup => statusGroup.tasks)
        .find(task => task.id.toString() === active.id);

      if (!taskToMove) return;

      // OPTIMISTIC UPDATE: Update UI immediately for smooth experience
      const updatedTasks = tasks.map(statusGroup => {
        if (statusGroup.status_title === taskToMove.status) {
          // Remove task from source column
          return {
            ...statusGroup,
            tasks: statusGroup.tasks.filter(task => task.id !== taskToMove.id)
          };
        } else if (statusGroup.status_title === newStatus) {
          // Add task to destination column
          const updatedTask = { ...taskToMove, status: newStatus };
          return {
            ...statusGroup,
            tasks: [...statusGroup.tasks, updatedTask]
          };
        }
        return statusGroup;
      });

      // Update UI immediately
      setTasks(updatedTasks);

      // Set loading state for visual feedback
      setIsUpdatingStatus(true);

      // Call API to update task status in background
      const response = await dataAPI.updateTaskStatus(taskToMove.id, newStatus);
      
      if (response.status === 'success') {
        // No need to refresh - UI is already updated
        setStatusUpdateError(''); // Clear any previous errors
      } else {
        // Revert optimistic update on failure
        setStatusUpdateError(`Failed to move task: ${response.message}`);
        setTimeout(() => setStatusUpdateError(''), 5000); // Auto-hide after 5 seconds
        await fetchProjectTasks(); // Refresh to get original state
      }
    } catch (error) {
      // Revert optimistic update on error
      setStatusUpdateError(`Failed to move task: ${error.message}`);
      setTimeout(() => setStatusUpdateError(''), 5000); // Auto-hide after 5 seconds
      await fetchProjectTasks(); // Refresh to get original state
    } finally {
      // Always clear the dragged task and loading state
      setDraggedTask(null);
      setIsUpdatingStatus(false);
    }
  };

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleFormClose = () => {
    setIsFormOpen(false);
    setIsEditMode(false);
    setEditingTask(null);
  };

  const handleFormSubmit = (response) => {
    setIsFormOpen(false);
    setIsEditMode(false);
    setEditingTask(null);
    // Refresh tasks list after creating/updating a task
    fetchProjectTasks();
  };

  const handleTaskOptionsClick = (task, action) => {
    if (action === 'edit') {
      handleEditTask(task);
    } else if (action === 'delete') {
      handleDeleteTask(task);
    }
  };

  // Helper functions for task display
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return '#f44336';
      case 'high':
        return '#ff9800';
      case 'medium':
        return '#ffc107';
      case 'low':
        return '#4caf50';
      default:
        return '#666';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return '#4CAF50';
      case 'in review':
        return '#FFC107'; // Brighter yellow for better visibility
      case 'in progress':
        return '#26C3C0';
      case 'to do':
        return '#6C757D'; // Grey color instead of white for visibility
      default:
        return '#666';
    }
  };

  // Function to get text color for status headers (ensures visibility in both themes)
  const getStatusTextColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return '#4CAF50';
      case 'in review':
        return '#F57C00'; // Darker orange-yellow for better readability
      case 'in progress':
        return '#26C3C0';
      case 'to do':
        return mode === 'dark' ? '#FFFFFF' : '#495057'; // Dynamic color for proper contrast
      default:
        return mode === 'dark' ? '#FFFFFF' : '#222222';
    }
  };

  // Helper function to get total task count across all status groups
  const getTotalTaskCount = () => {
    return tasks.reduce((total, statusGroup) => total + statusGroup.tasks.length, 0);
  };

  // Ensure we always have all four status columns
  const getStatusColumns = () => {
    const defaultStatuses = [
      { status_title: 'To Do', tasks: [] },
      { status_title: 'In Progress', tasks: [] },
      { status_title: 'In Review', tasks: [] },
      { status_title: 'Completed', tasks: [] }
    ];

    if (tasks.length === 0) {
      return defaultStatuses;
    }

    // Merge API data with default statuses to ensure all columns exist
    const statusMap = new Map();
    defaultStatuses.forEach(status => {
      statusMap.set(status.status_title, status);
    });

    tasks.forEach(statusGroup => {
      if (statusMap.has(statusGroup.status_title)) {
        statusMap.set(statusGroup.status_title, statusGroup);
      }
    });

    return Array.from(statusMap.values());
  };

  if (isLoading) {
    return (
      <div style={{
        padding: '24px',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          fontSize: '18px',
          color: mode === 'dark' ? '#FFFFFF' : '#222222',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          Loading tasks...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '24px',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '18px',
          color: '#f44336',
          marginBottom: '16px',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          {error}
        </div>
        <button 
          onClick={() => window.location.reload()}
          style={{
            background: '#26C3C0',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
            fontFamily: 'Urbanist, Arial, sans-serif',
            transition: 'background-color 0.2s ease'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  // Empty Tasks State for specific project
  return (
    <>
      <style>
        {`
          .kanban-board {
            scrollbar-width: none;
            -ms-overflow-style: none;
            width: 100%;
            overflow-x: auto !important;
            overflow-y: hidden !important;
            position: relative;
            z-index: 1;
            /* Ensure kanban board is the only element that scrolls horizontally */
            max-width: none !important;
            min-width: 1280px !important;
          }
          
          /* Ensure header doesn't scroll horizontally */
          .header-container {
            overflow-x: hidden !important;
            width: 100% !important;
            max-width: 100% !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 10 !important;
          }
          
          /* Prevent any horizontal scroll on header elements */
          .header-container * {
            overflow-x: hidden !important;
          }
          .kanban-board::-webkit-scrollbar {
            height: 0px;
            width: 0px;
            display: none;
          }
          .kanban-board::-webkit-scrollbar-track {
            display: none;
          }
          .kanban-board::-webkit-scrollbar-thumb {
            display: none;
          }
          .kanban-board::-webkit-scrollbar-thumb:hover {
            display: none;
          }
          
          /* Hide scrollbar on the kanban container wrapper */
          .kanban-board::-webkit-scrollbar,
          .kanban-board::-webkit-scrollbar-track,
          .kanban-board::-webkit-scrollbar-thumb {
            display: none !important;
          }
          
          /* Target the direct parent container that has overflow-x: auto */
          div[style*="overflow-x: auto"]::-webkit-scrollbar {
            height: 0 !important;
            width: 0 !important;
            display: none !important;
          }
          
          div[style*="overflow-x: auto"]::-webkit-scrollbar-track {
            display: none !important;
          }
          
          div[style*="overflow-x: auto"]::-webkit-scrollbar-thumb {
            display: none !important;
          }
          .task-card {
            transition: all 0.2s ease;
          }
          .task-card:hover {
            transform: translateY(-2px);
            box-shadow: ${mode === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.15)'};
          }
          
          /* Ensure columns maintain their width */
          .kanban-board > div {
            min-width: 300px;
            max-width: 340px;
          }
          
          /* Responsive design for mobile */
          @media (max-width: 1200px) {
            .kanban-board {
              grid-template-columns: repeat(2, minmax(300px, 1fr)) !important;
              min-width: 640px !important;
            }
          }
          
          @media (max-width: 768px) {
            .kanban-board {
              grid-template-columns: 1fr !important;
              gap: 12px !important;
              min-width: auto !important;
            }
            .kanban-board > div {
              min-width: auto !important;
              max-width: none !important;
            }
          }
        `}
      </style>
      <div style={{
        padding: '24px',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        minHeight: '100vh',
        overflowX: 'hidden' // Prevent horizontal scroll on main container
      }}>
        {/* Header with Company Logo, Project Title, and Created By - Fixed Position */}
        <div 
          className="header-container"
          style={{
            position: 'sticky',
            top: '0',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '32px',
            padding: '16px',
            backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
            borderRadius: '8px',
            boxShadow: mode === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden'
          }}
        >
          {/* Company Logo */}
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '6px',
            backgroundColor: mode === 'dark' ? '#3a3a3a' : '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            {companyLogo ? (
              <img
                src={getProfileCompanyPicUrl(companyLogo)}
                alt="Company Logo"
                style={{
                  width: '26px',
                  height: '26px',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  // Replace the image with a fallback avatar when it fails to load
                  const fallbackDiv = document.createElement('div');
                  fallbackDiv.style.cssText = `
                    width: 26px;
                    height: 26px;
                    border-radius: 6px;
                    background-color: #26C3C0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #FFFFFF;
                    font-size: 16px;
                    font-weight: 600;
                  `;
                  fallbackDiv.textContent = companyName ? companyName.charAt(0).toUpperCase() : 'C';
                  e.target.parentNode.replaceChild(fallbackDiv, e.target);
                }}
              />
            ) : (
              <span style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#FFFFFF',
                backgroundColor: '#26C3C0',
                width: '26px',
                height: '26px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {companyName ? companyName.charAt(0).toUpperCase() : 'C'}
              </span>
            )}
          </div>
          
          {/* Project Title and Created By */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: mode === 'dark' ? '#FFFFFF' : '#222222',
              margin: '0 0 2px 0',
              fontFamily: 'Urbanist, Arial, sans-serif',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {projectDetails?.title || 'Project Title'}
            </h1>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '12px',
              color: mode === 'dark' ? '#999' : '#666',
              fontFamily: 'Urbanist, Arial, sans-serif'
            }}>
              <span>By:</span>
              <span>{projectDetails?.created_by_username || 'Unknown'}</span>
            </div>
          </div>
        </div>

        {/* Project Tasks Content */}
        {getStatusColumns().some(statusGroup => statusGroup.tasks.length > 0) ? (
          // Kanban Board View
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
                         {/* Kanban Board */}
             <div style={{
               overflowX: 'auto',
               overflowY: 'hidden',
               width: '100%',
               maxWidth: '100%',
               scrollbarWidth: 'none',
               msOverflowStyle: 'none'
             }}>
               <DndContext
                 sensors={sensors}
                 collisionDetection={closestCenter}
                 onDragStart={(event) => {
                   const task = tasks
                     .flatMap(statusGroup => statusGroup.tasks)
                     .find(task => task.id.toString() === event.active.id);
                   setDraggedTask(task);
                 }}
                 onDragEnd={handleDragEnd}
                 onDragCancel={() => setDraggedTask(null)}
               >
              <style>
                {`
                  .kanban-board .dnd-kit-dragging {
                    transform: rotate(2deg) !important;
                    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3) !important;
                    opacity: 0.8 !important;
                  }
                  
                  .kanban-board .dnd-kit-dragging-over {
                    background-color: ${mode === 'dark' ? '#333' : '#f0f0f0'} !important;
                    border-radius: 8px !important;
                    padding: 8px !important;
                  }
                  
                  .kanban-column {
                    transition: all 0.2s ease;
                  }
                  
                  .kanban-column:hover {
                    background-color: ${mode === 'dark' ? '#2f2f2f' : '#f5f5f5'} !important;
                  }
                  
                  .kanban-column.dnd-kit-dragging-over {
                    background-color: ${mode === 'dark' ? '#2f2f2f' : '#f5f5f0'} !important;
                    border: 2px dashed #26C3C0 !important;
                  }
                  
                  .kanban-column[data-status] {
                    transition: all 0.2s ease;
                  }
                  
                  .kanban-column[data-status]:hover {
                    background-color: ${mode === 'dark' ? '#2f2f2f' : '#f5f5f5'} !important;
                  }
                  
                  .task-card {
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
                  }
                  
                  .task-card:hover {
                    transform: translateY(-4px) !important;
                    box-shadow: ${mode === 'dark' ? '0 8px 16px rgba(0, 0, 0, 0.4)' : '0 8px 16px rgba(0, 0, 0, 0.2)'} !important;
                    border-color: #26C3C0 !important;
                    z-index: 10 !important;
                  }
                  
                  .task-card:hover * {
                    transform: none !important;
                    box-shadow: none !important;
                  }
                  
                  .task-card img,
                  .task-card div[style*="border-radius: 50%"] {
                    box-shadow: none !important;
                  }
                  
                  .task-card * {
                    box-shadow: none !important;
                  }
                  
                  .task-card:hover * {
                    box-shadow: none !important;
                  }
                  
                  @keyframes pulse {
                    0%, 100% {
                      opacity: 1;
                      transform: scale(1);
                    }
                    50% {
                      opacity: 0.5;
                      transform: scale(1.2);
                    }
                  }
                `}
              </style>
              <div 
                className="kanban-board"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(300px, 1fr))',
                  gap: '16px',
                  overflowX: 'auto',
                  paddingBottom: '20px',
                  minHeight: '500px',
                  minWidth: '1280px'
                }}
              >
                {getStatusColumns().map((statusGroup) => (
                  <div key={statusGroup.status_title} style={{
                    backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                    border: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
                    borderRadius: '12px',
                    padding: '20px 5px',
                    minHeight: '400px',
                    minWidth: '300px',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: mode === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}>
                    {/* Column Header with Status Color Bar */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '16px',
                      paddingBottom: '12px',
                      borderBottom: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {/* Status Color Bar */}
                        <div style={{
                          width: '4px',
                          height: '20px',
                          backgroundColor: getStatusColor(statusGroup.status_title),
                          borderRadius: '2px'
                        }} />
                        <h3 style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: getStatusTextColor(statusGroup.status_title),
                          margin: 0,
                          fontFamily: 'Urbanist, Arial, sans-serif'
                        }}>
                          {statusGroup.status_title}
                        </h3>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{
                          padding: '4px 8px',
                          backgroundColor: mode === 'dark' ? '#444' : '#e0e0e0',
                          color: mode === 'dark' ? '#FFFFFF' : '#666666',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          fontFamily: 'Urbanist, Arial, sans-serif',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          {statusGroup.tasks.length.toString().padStart(2, '0')}
                          {isUpdatingStatus && (
                            <div style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: '#26C3C0',
                              animation: 'pulse 1.5s ease-in-out infinite'
                            }} />
                          )}
                        </span>
                        <button
                          onClick={handleCreateTask}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            borderRadius: '4px',
                            color: mode === 'dark' ? '#FFFFFF' : '#666666',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = mode === 'dark' ? '#333' : '#f0f0f0';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          <AddIcon style={{ fontSize: '18px' }} />
                        </button>
                      </div>
                    </div>

                    {/* Tasks in this column */}
                    <DroppableColumn status={statusGroup.status_title} mode={mode}>
                      <SortableContext
                        items={statusGroup.tasks.map(task => task.id.toString())}
                        strategy={verticalListSortingStrategy}
                      >
                        {statusGroup.tasks.length > 0 ? (
                          statusGroup.tasks.map((task) => (
                            <SortableTaskCard
                              key={task.id}
                              task={task}
                              onOptionsClick={handleTaskOptionsClick}
                              onCardClick={handleViewTask}
                              status={statusGroup.status_title}
                            />
                          ))
                        ) : (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100px',
                            color: mode === 'dark' ? '#666' : '#999',
                            fontSize: '14px',
                            fontFamily: 'Urbanist, Arial, sans-serif',
                            fontStyle: 'italic',
                            border: `2px dashed ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
                            borderRadius: '8px',
                            margin: '8px'
                          }}>
                            Drop tasks here
                          </div>
                        )}
                      </SortableContext>
                    </DroppableColumn>
                  </div>
                ))}
                             </div>
               </DndContext>
             </div>
             
             {/* Drag Overlay - Shows the dragged task card */}
            <DragOverlay>
              {draggedTask ? (
                <TaskCard
                  task={draggedTask}
                  onOptionsClick={() => {}} // No actions during drag
                  onCardClick={() => {}} // No navigation during drag
                  isDragging={true}
                />
              ) : null}
            </DragOverlay>
            
            {/* Error Message Toast */}
            {statusUpdateError && (
              <div style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                backgroundColor: '#1a0000',
                border: '1px solid #ff4444',
                borderRadius: '8px',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: '0 2px 8px rgba(255, 68, 68, 0.2)',
                zIndex: 2000,
                minWidth: '350px',
                maxWidth: '400px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <WarningIcon style={{
                    color: '#ff4444',
                    fontSize: '20px'
                  }} />
                  <span style={{
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: 'Urbanist, Arial, sans-serif',
                    lineHeight: '1.4'
                  }}>
                    {statusUpdateError}
                  </span>
                </div>
              </div>
            )}

            {deleteError && (
              <div style={{
                position: 'fixed',
                top: statusUpdateError ? '100px' : '20px', // Stack below statusUpdateError if both are shown
                right: '20px',
                backgroundColor: '#1a0000',
                border: '1px solid #ff4444',
                borderRadius: '8px',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: '0 2px 8px rgba(255, 68, 68, 0.2)',
                zIndex: 2000,
                minWidth: '350px',
                maxWidth: '400px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <WarningIcon style={{
                    color: '#ff4444',
                    fontSize: '20px'
                  }} />
                  <span style={{
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: 'Urbanist, Arial, sans-serif',
                    lineHeight: '1.4'
                  }}>
                    {deleteError}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Empty Tasks State
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            minHeight: 'calc(100vh - 200px)'
          }}>
            {/* Empty Tasks Icon */}
            <div style={{
              marginBottom: '24px'
            }}>
              <img 
                src={emptyDocIcon} 
                alt="No Tasks" 
                style={{
                  width: '71px',
                  height: '59px',
                  filter: mode === 'dark' ? 'brightness(1)' : 'brightness(0.8)'
                }}
              />
            </div>

            {/* Title */}
            <h2 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: mode === 'dark' ? '#FFFFFF' : '#222222',
              margin: '0 0 16px 0',
              fontFamily: 'Urbanist, Arial, sans-serif',
              lineHeight: '1.2'
            }}>
              No Tasks for This Project Yet
            </h2>

            {/* Description */}
            <p style={{
              fontSize: '16px',
              color: mode === 'dark' ? '#FFFFFF' : '#666666',
              margin: '0 0 32px 0',
              maxWidth: '600px',
              lineHeight: '1.5',
              fontFamily: 'Urbanist, Arial, sans-serif'
            }}>
              Get started by creating your first task for this project. Tasks help you break down project work into manageable pieces, set priorities, and track progress effectively. Use the Kanban board to organize tasks by status and track progress visually.
            </p>

            {/* Create Task Button */}
            {hasAddPermission('task') && (
              <button 
                onClick={handleCreateTask}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#26C3C0',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  transition: 'background-color 0.2s ease',
                  boxShadow: '0 2px 8px rgba(38, 195, 192, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#22B0AD';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#26C3C0';
                }}
              >
                <AddIcon style={{ fontSize: '20px' }} />
                Create Task
              </button>
            )}
          </div>
        )}
      </div>

      {/* Task Form - Always render this so form can open */}
      {isFormOpen && (
        <TaskForm
          isOpen={isFormOpen}
          projectId={projectId}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          editData={editingTask}
          projectStartDate={projectDetails?.start_date}
        />
      )}

      {/* Alert Dialog for deletion confirmation */}
      <AlertDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDeleteTask}
        type="delete"
        title="Confirm Deletion"
        message={`Are you sure you want to delete "${taskToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default Tasks;
